import { Helmet } from 'react-helmet-async';

interface OrganizationData {
  type: 'Organization';
  name: string;
  url: string;
  logo?: string;
  description?: string;
  sameAs?: string[];
}

interface WebSiteData {
  type: 'WebSite';
  name: string;
  url: string;
  description?: string;
}

interface ProductData {
  type: 'Product';
  name: string;
  description: string;
  brand: string;
  image?: string;
  sku?: string;
  aggregateRating?: {
    ratingValue: string;
    reviewCount: string;
  };
  offers: {
    price: string;
    priceCurrency: string;
    availability: string;
    priceValidUntil?: string;
    url?: string;
  }[];
}

interface FAQData {
  type: 'FAQPage';
  questions: {
    question: string;
    answer: string;
  }[];
}

interface ArticleData {
  type: 'Article';
  headline: string;
  description: string;
  image?: string;
  author: string;
  datePublished: string;
  dateModified?: string;
  publisher: {
    name: string;
    logo?: string;
  };
}

interface BreadcrumbData {
  type: 'BreadcrumbList';
  items: {
    name: string;
    url: string;
  }[];
}

interface ContactPageData {
  type: 'ContactPage';
  name: string;
  description: string;
  url: string;
}

type StructuredDataProps = 
  | OrganizationData 
  | WebSiteData 
  | ProductData 
  | FAQData 
  | ArticleData 
  | BreadcrumbData
  | ContactPageData;

const generateOrganizationSchema = (data: OrganizationData) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": data.name,
  "url": data.url,
  ...(data.logo && { "logo": data.logo }),
  ...(data.description && { "description": data.description }),
  ...(data.sameAs && { "sameAs": data.sameAs }),
});

const generateWebSiteSchema = (data: WebSiteData) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": data.name,
  "url": data.url,
  ...(data.description && { "description": data.description }),
  "potentialAction": {
    "@type": "SearchAction",
    "target": `${data.url}/search?q={search_term_string}`,
    "query-input": "required name=search_term_string"
  }
});

const generateProductSchema = (data: ProductData) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": data.name,
  "description": data.description,
  "brand": {
    "@type": "Brand",
    "name": data.brand
  },
  ...(data.image && { "image": data.image }),
  ...(data.sku && { "sku": data.sku }),
  ...(data.aggregateRating && {
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": data.aggregateRating.ratingValue,
      "reviewCount": data.aggregateRating.reviewCount,
      "bestRating": "5",
      "worstRating": "1"
    }
  }),
  "offers": data.offers.map(offer => ({
    "@type": "Offer",
    "price": offer.price,
    "priceCurrency": offer.priceCurrency,
    "availability": offer.availability,
    ...(offer.priceValidUntil && { "priceValidUntil": offer.priceValidUntil }),
    ...(offer.url && { "url": offer.url })
  }))
});

const generateFAQSchema = (data: FAQData) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": data.questions.map(q => ({
    "@type": "Question",
    "name": q.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": q.answer
    }
  }))
});

const generateArticleSchema = (data: ArticleData) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": data.headline,
  "description": data.description,
  ...(data.image && { "image": data.image }),
  "author": {
    "@type": "Person",
    "name": data.author
  },
  "datePublished": data.datePublished,
  ...(data.dateModified && { "dateModified": data.dateModified }),
  "publisher": {
    "@type": "Organization",
    "name": data.publisher.name,
    ...(data.publisher.logo && { 
      "logo": {
        "@type": "ImageObject",
        "url": data.publisher.logo
      }
    })
  }
});

const generateBreadcrumbSchema = (data: BreadcrumbData) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": data.items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

const generateContactPageSchema = (data: ContactPageData) => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": data.name,
  "description": data.description,
  "url": data.url
});

export const StructuredData = (props: StructuredDataProps) => {
  let schema;
  
  switch (props.type) {
    case 'Organization':
      schema = generateOrganizationSchema(props);
      break;
    case 'WebSite':
      schema = generateWebSiteSchema(props);
      break;
    case 'Product':
      schema = generateProductSchema(props);
      break;
    case 'FAQPage':
      schema = generateFAQSchema(props);
      break;
    case 'Article':
      schema = generateArticleSchema(props);
      break;
    case 'BreadcrumbList':
      schema = generateBreadcrumbSchema(props);
      break;
    case 'ContactPage':
      schema = generateContactPageSchema(props);
      break;
    default:
      return null;
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default StructuredData;
